import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import {
  GroupStage,
  R16Stage,
  R8Stage,
  QuartersStage,
  SemiStage,
  FinalStage,
} from "./components";
import "./styles.scss";
import usePhaseTransition from "./utils/usePhaseTransition";
import useSportStage from "../hooks/useSportStages";
import chevronLeft from './assets/chevronizquierdo.png';
import chevronRight from './assets/chevronderecho.png';
import { useMundialMetrics } from '../hooks/useMundialMetrics';
import { INTERACTION_TYPES } from '../hooks/useMundialMetrics/constants';

function PhaseContent({ stageCode, stageData, nextStageData, thirdPlaceData, sub, setSubParam, mobileMenuOpen, onMobileMenuOpenChange, onMatchCardClick }) {
  switch (stageCode) {
    case "GROUP_STAGE":
      return <GroupStage stageData={stageData} mobileMenuOpen={mobileMenuOpen} onMobileGroupMenuOpenChange={onMobileMenuOpenChange} activeGroup={sub || "A"} onGroupChange={setSubParam} onMatchCardClick={onMatchCardClick} />;
    case "ROUND_OF_32":
      return <R16Stage stageData={stageData} nextStageData={nextStageData} mobileMenuOpen={mobileMenuOpen} onMobileRoundMenuOpenChange={onMobileMenuOpenChange} activeRound={sub != null ? Number(sub) : 0} onRoundChange={setSubParam} onMatchCardClick={onMatchCardClick} />;
    case "ROUND_OF_16":
      return <R8Stage stageData={stageData} nextStageData={nextStageData} mobileMenuOpen={mobileMenuOpen} onMobileRoundMenuOpenChange={onMobileMenuOpenChange} activeRound={sub != null ? Number(sub) : 0} onRoundChange={setSubParam} onMatchCardClick={onMatchCardClick} />;
    case "QUARTER_FINALS":
      return <QuartersStage stageData={stageData} nextStageData={nextStageData} mobileMenuOpen={mobileMenuOpen} onMobileRoundMenuOpenChange={onMobileMenuOpenChange} activeRound={sub != null ? Number(sub) : 0} onRoundChange={setSubParam} onMatchCardClick={onMatchCardClick} />;
    case "SEMI_FINALS, THIRD_PLACE":
      return <SemiStage stageData={stageData} nextStageData={nextStageData} thirdPlaceData={thirdPlaceData} onMatchCardClick={onMatchCardClick} />;
    case "SEMI_FINALS,THIRD_PLACE":
      return <SemiStage stageData={stageData} nextStageData={nextStageData} thirdPlaceData={thirdPlaceData} onMatchCardClick={onMatchCardClick} />;
    case "FINAL":
      return <FinalStage stageData={stageData} thirdPlaceData={thirdPlaceData} onMatchCardClick={onMatchCardClick} />;
    default:
      return null;
  }
}

function PeekLabel({ label, side }) {
  const isLeft = side === "left";
  return (
    <div className="bracket-card__peek">
      <div className="bracket-card__chevron">
        <img src={isLeft ? chevronLeft : chevronRight} alt="" />
      </div>
      <span className="bracket-card__label">{label}</span>
    </div>
  );
}

const MATCH_ID_PARAM = 'matchId';
const MOBILE_MEDIA = '(max-width: 1024px)';
const SWIPE_THRESHOLD_PX = 48;

function getInitialMobileMenuOpen() {
  if (typeof window === 'undefined') return true;
  return window.matchMedia?.(MOBILE_MEDIA).matches ?? true;
}

export default function WCCarousel({ onMatchCardClick: onMatchCardClickFromParent }) {
  const { stages, leagueId, fetchStage, stageData, nextStageData, thirdPlaceData, stageLoading } = useSportStage();

  const navigate = useNavigate();
  const location = useLocation();
  const defaultOnMatchCardClick = useCallback(
    (id) => {
      if (id == null || id === '') return;
      const params = new URLSearchParams(location.search);
      params.set(MATCH_ID_PARAM, String(id));
      navigate(`${location.pathname}?${params.toString()}`);
    },
    [navigate, location],
  );
  const onMatchCardClick = onMatchCardClickFromParent ?? defaultOnMatchCardClick;


  const { phase, goTo, getIndex, sub, setSubParam } = usePhaseTransition(stages);

  const { trackSelectContent } = useMundialMetrics();

  const [mobileFilterMenuOpen, setMobileFilterMenuOpen] = useState(getInitialMobileMenuOpen);
  const [loadingStageCode, setLoadingStageCode] = useState(null);
  const [renderedStageCode, setRenderedStageCode] = useState(null);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_MEDIA).matches,
  );
  const [stageHeight, setStageHeight] = useState(660);
  const phaseButtonsRef = useRef(null);
  const activePhaseButtonRef = useRef(null);
  const touchStartRef = useRef(null);
  const observerRef = useRef(null);
  const rafRef = useRef(null);

  const prevIndex = getIndex(phase - 1);
  const nextIndex = getIndex(phase + 1);

  const handlePhaseNavigation = useCallback((targetIndex, interactionType) => {
    const targetLabel = stages[targetIndex]?.label || '';
    trackSelectContent({
      pageTitleSegments: [targetLabel],
      pageTitle: targetLabel,
      contentSubsection: targetLabel,
      moduloName: '',
      interactionType,
    });
    goTo(targetIndex);
  }, [goTo, stages, trackSelectContent]);

  const handleBracketTouchStart = useCallback((event) => {
    if (typeof window === 'undefined' || !window.matchMedia?.(MOBILE_MEDIA).matches) return;

    const touch = event.touches?.[0];
    if (!touch) return;

    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  }, []);

  const handleBracketTouchEnd = useCallback((event) => {
    if (typeof window === 'undefined' || !window.matchMedia?.(MOBILE_MEDIA).matches) return;

    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start) return;

    const touch = event.changedTouches?.[0];
    if (!touch) return;

    const deltaX = touch.clientX - start.x;
    const deltaY = touch.clientY - start.y;

    if (Math.abs(deltaX) < SWIPE_THRESHOLD_PX) return;
    if (Math.abs(deltaX) <= Math.abs(deltaY)) return;

    if (deltaX < 0) {
      handlePhaseNavigation(nextIndex, INTERACTION_TYPES.SIDE_ARROWS);
      return;
    }

    handlePhaseNavigation(prevIndex, INTERACTION_TYPES.SIDE_ARROWS);
  }, [handlePhaseNavigation, nextIndex, prevIndex]);

  const handleBracketTouchCancel = useCallback(() => {
    touchStartRef.current = null;
  }, []);

  const currentStageCode = stages[phase]?.code;
  const isStageDataReady = renderedStageCode === currentStageCode && stageData != null;

  useEffect(() => {
    if (currentStageCode) {
      setLoadingStageCode(currentStageCode);
      setRenderedStageCode(null);
      fetchStage(currentStageCode);
    }
  }, [currentStageCode, fetchStage]);

  useEffect(() => {
    if (!currentStageCode || loadingStageCode !== currentStageCode || stageLoading || !stageData) return;

    setRenderedStageCode(currentStageCode);
    setLoadingStageCode(null);
  }, [currentStageCode, loadingStageCode, stageData, stageLoading]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia(MOBILE_MEDIA);
    const handler = e => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const contentCallbackRef = useCallback(el => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    if (!el || !isMobile) return;

    const observer = new ResizeObserver(entries => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        const entry = entries[0];
        if (!entry) return;
        const height = Math.max(
          660,
          Math.ceil(entry.borderBoxSize?.[0]?.blockSize ?? entry.target.offsetHeight),
        );
        setStageHeight(prev => (prev === height ? prev : height));
      });
    });

    observer.observe(el);
    observerRef.current = observer;
  }, [isMobile]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!window.matchMedia?.("(max-width: 1024px)").matches) return;

    const container = phaseButtonsRef.current;
    const activeButton = activePhaseButtonRef.current;
    if (!container || !activeButton) return;

    const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const behavior = prefersReducedMotion ? "auto" : "smooth";

    // Center the active tab within the horizontal scroll container (mobile only).
    requestAnimationFrame(() => {
      const containerRect = container.getBoundingClientRect();
      const buttonRect = activeButton.getBoundingClientRect();
      const targetLeft =
        container.scrollLeft +
        (buttonRect.left - containerRect.left) -
        (containerRect.width / 2 - buttonRect.width / 2);

      container.scrollTo({ left: targetLeft, behavior });
    });
  }, [phase]);

  const getPosition = (idx) => {
    if (idx === phase) return "center";
    if (idx === prevIndex) return "left";
    if (idx === nextIndex) return "right";
    return "hidden";
  };

  if (!stages.length) return null;

  return (
    <div className="world-cup-bracket">
      <div className="phase-buttons" ref={phaseButtonsRef}>
        {stages.map((s, i) => (
          <button
            key={s.code}
            onClick={() => handlePhaseNavigation(i, INTERACTION_TYPES.PHASE_TABS)}
            className={phase === i ? "active" : ""}
            ref={(el) => {
              if (phase === i) activePhaseButtonRef.current = el;
            }}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div
        className={`bracket-stage${
          phase === 0 && mobileFilterMenuOpen ? " bracket-stage--group-menu-open" : ""
        }${
          phase >= 1 && phase <= 3 && mobileFilterMenuOpen ? " bracket-stage--round-menu-open" : ""
        }`}
        style={isMobile ? { height: stageHeight } : undefined}
        onTouchStart={handleBracketTouchStart}
        onTouchEnd={handleBracketTouchEnd}
        onTouchCancel={handleBracketTouchCancel}
      >
        {stages.map((s, i) => {
          const position = getPosition(i);

          return (
            <div
              key={s.code}
              className={`bracket-card bracket-card--${position}`}
              style={position === 'center' && isMobile ? { height: stageHeight } : undefined}
              onClick={position !== "center" && position !== "hidden" ? () => handlePhaseNavigation(i, INTERACTION_TYPES.SIDE_ARROWS) : undefined}
            >
              {position === "center" ? (
                <div className="bracket-card__content" ref={contentCallbackRef}>
                  {isStageDataReady ? (
                    <PhaseContent
                      stageCode={s.code}
                      stageData={stageData}
                      nextStageData={nextStageData}
                      thirdPlaceData={thirdPlaceData}
                      sub={sub}
                      setSubParam={setSubParam}
                      mobileMenuOpen={mobileFilterMenuOpen}
                      onMobileMenuOpenChange={setMobileFilterMenuOpen}
                      onMatchCardClick={onMatchCardClick}
                    />
                  ) : null}
                </div>
              ) : (
                <PeekLabel label={s.label} side={position === "left" || position === "hidden" ? "left" : "right"} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
